import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { History, ChevronLeft, ChevronRight, Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

const PAGE_SIZE = 30;

export default function AuditPage() {
  const { t } = useTranslation();
  const [changeType, setChangeType] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', changeType, page],
    queryFn: () => api.getAuditLogs({
      change_type: changeType || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
  });

  const logs = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const changeTypeConfig: Record<string, { label: string; icon: any; color: string }> = {
    created: { label: t('audit.created'), icon: Plus, color: 'text-success-600 bg-success-100' },
    updated: { label: t('audit.updated'), icon: Pencil, color: 'text-info-600 bg-info-100' },
    deleted: { label: t('audit.deleted'), icon: Trash2, color: 'text-danger-600 bg-danger-100' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title">{t('audit.title')}</h1>
        <select
          value={changeType}
          onChange={(e) => { setChangeType(e.target.value); setPage(0); }}
          className="input py-2 text-sm w-auto"
        >
          <option value="">{t('audit.allActions')}</option>
          <option value="created">{t('audit.createAction')}</option>
          <option value="updated">{t('audit.updateAction')}</option>
          <option value="deleted">{t('audit.deleteAction')}</option>
        </select>
      </div>

      {isLoading && <div className="flex justify-center py-12"><div className="spinner"></div></div>}

      <div className="text-sm text-surface-500">{total} {t('common.records')}</div>

      <div className="grid gap-3">
        {logs.length > 0 ? logs.map((log) => {
          const config = changeTypeConfig[log.change_type] || changeTypeConfig.created;
          const Icon = config.icon;
          return (
            <div key={log.id} className="card">
              <div className="flex items-start gap-4">
                <div className={`p-2 rounded-lg ${config.color.split(' ')[1]}`}>
                  <Icon className={config.color.split(' ')[0]} size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-surface-900">
                        {log.changed_by_name}
                        <span className={`mx-2 px-2 py-0.5 rounded text-xs ${config.color}`}>{config.label}</span>
                        {t('audit.workLog')}
                      </p>
                      <p className="text-sm text-surface-500 mt-1">{t('audit.ticketLabel')}{log.ticket_title}</p>
                    </div>
                    <span className="text-sm text-surface-400">
                      {format(new Date(log.changed_at), 'dd/MM/yyyy HH:mm', { locale: he })}
                    </span>
                  </div>

                  {log.change_type === 'updated' && log.old_values && log.new_values && (
                    <div className="mt-3 text-sm bg-surface-50 rounded-lg p-3 space-y-1">
                      {log.old_values.summary !== log.new_values.summary && (
                        <div>
                          <span className="text-surface-500">{t('audit.summaryLabel')}</span>
                          <span className="line-through text-red-400">{log.old_values.summary?.substring(0, 60)}</span>
                          <span className="text-success-600"> {log.new_values.summary?.substring(0, 60)}</span>
                        </div>
                      )}
                      {log.old_values.billable !== log.new_values.billable && (
                        <div>
                          <span className="text-surface-500">{t('audit.billableLabel')}</span>
                          <span className="line-through text-red-400">{log.old_values.billable ? t('common.yes') : t('common.no')}</span>
                          <span className="text-success-600"> {log.new_values.billable ? t('common.yes') : t('common.no')}</span>
                        </div>
                      )}
                      {log.old_values.deleted_at !== log.new_values.deleted_at && log.new_values.deleted_at && (
                        <div className="text-danger-600">{t('audit.softDeleted')}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }) : !isLoading && (
          <div className="card text-center py-12">
            <History className="mx-auto text-surface-400 mb-4" size={48} />
            <p className="text-surface-600">{t('audit.noRecords')}</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronRight size={20} />
          </button>
          <span className="text-sm text-surface-600">{t('common.page')} {page + 1} {t('common.of')} {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronLeft size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
